// useNodeDescendants.js
// Hook for reading descendant nodes using path (subscription + cached snapshot).

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getDescendantNodesForUser,
  subscribeToDescendantNodes,
} from 'services/nodes/NodeReadService';

const useSubscribedDescendants = (nodeKey, user, parentNode, enabled) => {
  const [descendants, setDescendants] = useState([]);

  useEffect(() => {
	if (!enabled || !nodeKey || !user) {
      setDescendants([]);
      return undefined;
    }

	const unsubscribe = subscribeToDescendantNodes(nodeKey, user, parentNode, setDescendants);
    return () => unsubscribe?.();
	}, [enabled, nodeKey, user, parentNode]);

  return descendants;
};

export default function useNodeDescendants({ nodeKey, user, parentNode, enabled = false }) {
  const queryClient = useQueryClient();

  const descendantsQueryKey = useMemo(
    () => ['node', nodeKey ?? null, 'descendants', user?.key ?? null],
    [nodeKey, user?.key]
  );

  const descendantsQuery = useQuery({
    queryKey: descendantsQueryKey,
    queryFn: () => getDescendantNodesForUser(nodeKey, user, parentNode),
    enabled: Boolean(enabled && nodeKey && user),
    staleTime: 30_000,
    gcTime: 300_000,
  });

  const subscribedDescendants = useSubscribedDescendants(nodeKey, user, parentNode, enabled);

  useEffect(() => {
    if (!enabled || !nodeKey) return;
    if (!Array.isArray(subscribedDescendants)) return;
    queryClient.setQueryData(descendantsQueryKey, subscribedDescendants);
  }, [enabled, nodeKey, subscribedDescendants, queryClient, descendantsQueryKey]);

  const descendantNodes = useMemo(() => {
    if (!enabled) return [];
    if (Array.isArray(subscribedDescendants) && subscribedDescendants.length > 0) {
      return subscribedDescendants;
    }
    if (Array.isArray(descendantsQuery.data)) {
      return descendantsQuery.data;
    }
    return Array.isArray(subscribedDescendants) ? subscribedDescendants : [];
  }, [enabled, subscribedDescendants, descendantsQuery.data]);

  return {
    descendantNodes,
  };
}
